import {
  faCalendar,
  faDatabase,
  faPaperPlane,
  faUser,
} from '@fortawesome/duotone-regular-svg-icons';
import {
  faArrowDownWideShort,
  faArrowUpWideShort,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';

import type {
  DtoV1LoanCommentsLoanCommentData as Comment,
  DtoV1LoanCommentsLoanCommentsDataResponse as LoanCommentsData,
} from '~/__generated__/api';
import Box from '~/common/components/Box';
import Button from '~/common/components/Button';
import FormLabel from '~/common/components/FormLabel';
import Oops from '~/common/components/Oops';
import Select from '~/common/components/Selects/Select';
import StandaloneSnackbar from '~/common/components/Snackbar/StandaloneSnackbar';
import { useStandaloneSnackbar } from '~/common/components/Snackbar/useStandaloneSnackbar';
import Spinner from '~/common/components/Spinner';
import Text from '~/common/components/Texts/Text';
import useUrl from '~/common/hooks/useUrl';
import IconButton from '~/common/mui/Buttons/MUIButton/MUIIconButton';
import MUIDialog from '~/common/mui/MUIDialog/MUIDialog';
import { typedApiClient } from '~/common/util/api-client';
import palette from '~/theme/mui/palette';

import { useModalState } from '../modalStore';
import {
  type CommentFormData,
  type LoanCommentCategoryOption,
  useAppForm,
  useStore,
  withForm,
} from './hooks/LoanCommentsFormHook';

const loanCommentsQueryKeyRoot = 'loanComments';
const defaultCategoryKey = 2000008;
const emptyCommentMessage = "Can't submit an empty comment";

function getLoanCommentsQueryKey(
  obfuscatedLenderDatabaseId: string | number,
  obfuscatedLoanRecordId: string | number,
) {
  return [
    loanCommentsQueryKeyRoot,
    obfuscatedLenderDatabaseId,
    obfuscatedLoanRecordId,
  ] as const;
}

type LoanCommentsDialogProps = Readonly<{
  data: LoanCommentsData;
}>;

type LoanCommentsSubmitMeta = {
  closeOnSuccess: boolean;
};

const defaultLoanCommentsSubmitMeta: LoanCommentsSubmitMeta = {
  closeOnSuccess: false,
};

function getCommentCategoryOptions(
  data: LoanCommentsData,
): LoanCommentCategoryOption[] {
  return (data.dropDownOptions?.loanCommentSubCategory ?? [])
    .filter(
      (
        option,
      ): option is {
        key: number;
        value?: string | null;
      } => typeof option?.key === 'number',
    )
    .map((option) => ({
      key: option.key,
      value: option.value ?? undefined,
    }));
}

function getDefaultCategory(
  options: LoanCommentCategoryOption[],
): LoanCommentCategoryOption {
  return (
    options.find((option) => option.key === defaultCategoryKey) ??
    options[0] ?? {
      key: defaultCategoryKey,
      value: 'General Comments',
    }
  );
}

function validateCommentValue({
  value,
}: {
  value: string;
}): string | undefined {
  return value.trim() === '' ? emptyCommentMessage : undefined;
}

export function LoanCommentsContainer() {
  const { isOpen, onClose } = useModalState('loanComments');
  const ANCHOR_SELECTOR = '[data-modal-anchor="loanComments"]';
  const { obfuscatedLoanIdentifier } = useUrl();
  const loanCommentsQueryKey = getLoanCommentsQueryKey(
    obfuscatedLoanIdentifier?.obfuscatedLenderDatabaseId,
    obfuscatedLoanIdentifier?.obfuscatedLoanRecordId,
  );

  const {
    data: commentData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: loanCommentsQueryKey,
    queryFn: async () => {
      const { data } = await typedApiClient.v1.loanCommentsCommentsList(
        obfuscatedLoanIdentifier?.obfuscatedLenderDatabaseId,
        obfuscatedLoanIdentifier?.obfuscatedLoanRecordId,
      );
      return data;
    },
    enabled: isOpen,
  });

  if (!isOpen) return null;

  if (isLoading)
    return (
      <MUIDialog
        title="Comments"
        dialogPosition="left"
        open={isOpen}
        onClose={onClose}
        anchorSelector={ANCHOR_SELECTOR}
        paperSx={{ width: '37.5rem', height: '45.5rem' }}
      >
        <Spinner backgroundColor="transparent" />
      </MUIDialog>
    );

  if (isError)
    return (
      <MUIDialog
        title="Comments"
        paperSx={{ width: '37.5rem', height: '45.5rem' }}
        dialogPosition="left"
        open={isOpen}
        onClose={onClose}
        anchorSelector={ANCHOR_SELECTOR}
      >
        <Oops />
      </MUIDialog>
    );

  return (
    <LoanCommentsDialog data={commentData} anchorSelector={ANCHOR_SELECTOR} />
  );
}

function LoanCommentsDialog({
  data,
  anchorSelector,
}: LoanCommentsDialogProps & { anchorSelector: string | null }) {
  const queryClient = useQueryClient();
  const { isOpen, onClose } = useModalState('loanComments');
  const [isCommentBoxFocused, setIsCommentBoxFocused] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [isDescending, setIsDescending] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { obfuscatedLoanIdentifier } = useUrl();
  const loanCommentsQueryKey = getLoanCommentsQueryKey(
    obfuscatedLoanIdentifier?.obfuscatedLenderDatabaseId,
    obfuscatedLoanIdentifier?.obfuscatedLoanRecordId,
  );

  const { snackbarProps, showSnackbar } = useStandaloneSnackbar();

  const loanCommentSubCategory = useMemo(
    () => getCommentCategoryOptions(data),
    [data],
  );

  const [initialCategory] = useState<LoanCommentCategoryOption>(() =>
    getDefaultCategory(loanCommentSubCategory),
  );

  const form = useAppForm({
    defaultValues: {
      category: initialCategory,
      comment: '',
    },
    onSubmitMeta: defaultLoanCommentsSubmitMeta,
    onSubmit: ({ value, meta }) => {
      mutate(value, {
        onSuccess: () => {
          if (meta.closeOnSuccess) {
            setShowUnsavedDialog(false);
            onClose();
          }
        },
      });
    },
  });

  const { mutate, isLoading: isSubmitting } = useMutation({
    mutationFn: async (formData: CommentFormData) => {
      const payload = {
        obfuscatedLoanIdentifier,
        commentText: formData.comment,
        commentSubCategoryId: formData.category.key,
      };
      await typedApiClient.v1.loanCommentsComments(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: loanCommentsQueryKey });
      showSnackbar({
        severity: 'success',
        message: 'Comment saved successfully',
        duration: 2500,
      });
      form.setFieldValue('comment', '');
      setIsCommentBoxFocused(false);
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message:
          'There was an error saving your comment. Try again, and submit a ticket via YouSupport if the issue persists.',
        duration: 5000,
      });
    },
  });

  const selectedCategory = useStore(
    form.store,
    (state) => state.values.category,
  );
  const commentValue = useStore(form.store, (state) => state.values.comment);

  const visibleComments = useMemo(() => {
    const commentsForSelectedCategory =
      data.loanCommentDetails?.[String(selectedCategory?.key ?? '')] ?? [];

    if (commentsForSelectedCategory.length > 0) {
      const sortedComments = commentsForSelectedCategory
        .map((comment) => ({
          comment,
          timestamp: new Date(comment.commentDateTime).getTime(),
        }))
        .toSorted((a, b) =>
          isDescending ? b.timestamp - a.timestamp : a.timestamp - b.timestamp,
        )
        .map(({ comment }) => comment);

      const normalizedSearchTerm = searchTerm.trim().toLowerCase();
      if (!normalizedSearchTerm) return sortedComments;

      return sortedComments.filter((comment) => {
        const commentText = comment.commentText?.toLowerCase() ?? '';
        const createdBy = comment.commentCreatedByUserName?.toLowerCase() ?? '';
        return (
          commentText.includes(normalizedSearchTerm) ||
          createdBy.includes(normalizedSearchTerm)
        );
      });
    }
    return [];
  }, [
    data.loanCommentDetails,
    isDescending,
    searchTerm,
    selectedCategory?.key,
  ]);

  const handleDialogClose = () => {
    if (commentValue?.trim()) {
      setShowUnsavedDialog(true);
      return;
    }

    onClose();
  };

  const handleKeepEditing = () => {
    setShowUnsavedDialog(false);
  };

  const handleDiscard = () => {
    form.reset({
      category: form.getFieldValue('category'),
      comment: '',
    });
    setShowUnsavedDialog(false);
    onClose();
  };

  const handleSubmitComment = () => {
    void form.handleSubmit();
  };

  const handleSubmitAndClose = () => {
    void form.handleSubmit({ closeOnSuccess: true });
  };

  return (
    <form.AppForm>
      <>
        <MUIDialog
          title="Comments"
          open={isOpen}
          onClose={handleDialogClose}
          paperSx={{ width: '37.5rem', height: '45.5rem', overflow: 'hidden' }}
          dialogPosition="left"
          anchorSelector={anchorSelector}
          dividers={true}
          contentSx={{ backgroundColor: 'gray.100' }}
          headerControls={
            <CommentControls
              form={form}
              isSubmitting={isSubmitting}
              options={loanCommentSubCategory}
              isDescending={isDescending}
              setIsDescending={setIsDescending}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
          }
          actions={
            <CommentInput
              form={form}
              setIsCommentBoxFocused={setIsCommentBoxFocused}
              isCommentBoxFocused={isCommentBoxFocused}
              isSubmitting={isSubmitting}
              onSubmitComment={handleSubmitComment}
            />
          }
        >
          {visibleComments.length === 0 && (
            <Text
              sx={{ color: 'gray.700' }}
            >{`There are no comments for ${selectedCategory?.value}`}</Text>
          )}
          {visibleComments.map((comment, index) => {
            const previousComment = visibleComments[index - 1];
            const showDateHeader =
              index === 0 ||
              previousComment?.commentDate !== comment.commentDate;

            return (
              <Box key={comment.commentId}>
                {showDateHeader && (
                  <Text
                    as="h3"
                    sx={{
                      color: 'gray.700',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      mt: index === 0 ? 0 : '1rem',
                      mb: '0.5rem',
                    }}
                  >
                    {comment.commentDate}
                  </Text>
                )}
                <CommentCard comment={comment} />
              </Box>
            );
          })}
          <StandaloneSnackbar
            {...snackbarProps}
            sx={{ position: 'absolute' }}
            isTopLevel={false}
          />
        </MUIDialog>

        <MUIDialog
          title="Close Without Submitting Comment?"
          open={showUnsavedDialog}
          onClose={handleKeepEditing}
          dialogPosition="center"
          hideBackdrop={false}
          paperSx={{ width: '35rem' }}
          actions={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                padding: '1rem 1.5rem',
              }}
            >
              <Button
                isPrimary={false}
                variant="ghost"
                onClick={handleSubmitAndClose}
              >
                Submit Comment
              </Button>
              <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                <Button isPrimary={false} onClick={handleKeepEditing}>
                  Keep Editing
                </Button>
                <Button isPrimary onClick={handleDiscard}>
                  Discard
                </Button>
              </Box>
            </Box>
          }
        >
          <Text
            sx={{ fontWeight: 400, whiteSpace: 'pre-wrap', marginTop: '1rem' }}
          >
            Comment will not save.
          </Text>
        </MUIDialog>
      </>
    </form.AppForm>
  );
}

const CommentCard = ({ comment }: { comment: Comment }) => {
  const {
    commentText,
    commentDate,
    commentTime,
    commentCreatedByUserName,
    isServerUser,
  } = comment;

  return (
    <Card
      variant="outlined"
      sx={{ mb: '0.5rem', borderRadius: '8px', fontSize: '0.92rem' }}
    >
      <CardContent sx={{ paddingBottom: '1rem !important' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            gap: '1rem',
            mb: '0.5rem',
          }}
        >
          <Box sx={{ display: 'flex', gap: '0.5rem' }}>
            <FontAwesomeIcon color={palette.blue[600]} icon={faCalendar} />
            <Text
              sx={{
                color: palette.blue[600],
                fontWeight: 500,
                fontSize: '0.75rem',
              }}
            >
              {commentDate} &bull; {commentTime}
            </Text>
          </Box>
          <Box sx={{ display: 'flex', gap: '0.5rem' }}>
            <FontAwesomeIcon
              color={palette.blue[600]}
              icon={isServerUser ? faDatabase : faUser}
            />
            <Text
              sx={{
                color: palette.blue[600],
                fontWeight: 500,
                fontSize: '0.75rem',
              }}
            >
              {commentCreatedByUserName}
            </Text>
          </Box>
        </Box>
        <Text as="h3" sx={{ fontWeight: 400 }}>
          {commentText}
        </Text>
      </CardContent>
    </Card>
  );
};

type CommentControlsProps = {
  isSubmitting: boolean;
  options: LoanCommentCategoryOption[];
  setIsDescending: Dispatch<SetStateAction<boolean>>;
  isDescending: boolean;
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
};

const CommentControls = withForm({
  props: {} as CommentControlsProps,
  defaultValues: {} as CommentFormData,
  render: function CommentControls({
    form,
    isSubmitting,
    options,
    setIsDescending,
    isDescending,
    searchTerm,
    setSearchTerm,
  }) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          flexWrap: 'nowrap',
          gap: '0.5rem',
          width: '100%',
          mt: 3,
        }}
      >
        <FormLabel
          htmlFor="category"
          label={'View by'}
          sx={{ whiteSpace: 'nowrap', mr: '0.25rem' }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <form.Field name="category">
            {(field) => (
              <Select
                id="category"
                name={field.name}
                aria-label="View by"
                isDisabled={isSubmitting}
                onBlur={() => field.handleBlur()}
                onChange={(e) => {
                  const selectedOption = options.find(
                    (option) => option.key === Number(e.target.value),
                  );

                  if (selectedOption) {
                    field.handleChange(selectedOption);
                  }
                }}
                value={field.state.value?.key ?? ''}
                sx={{ color: `${palette.black}` }}
              >
                {options.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.value}
                  </option>
                ))}
              </Select>
            )}
          </form.Field>
        </Box>
        <IconButton
          sx={{
            border: `2px solid ${palette.blue[600]}`,
            borderRadius: '8px',
            width: '2.5rem',
            height: '2.5rem',
          }}
          aria-label={`Sort comments from ${isDescending ? 'oldest to most recent' : 'most recent to oldest'}`}
          onClick={() => setIsDescending((value) => !value)}
        >
          <FontAwesomeIcon
            color={palette.blue[600]}
            icon={isDescending ? faArrowDownWideShort : faArrowUpWideShort}
          />
        </IconButton>
        <TextField
          id="search-comments"
          aria-label="Search Comments"
          placeholder="Search Comments"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={isSubmitting}
          sx={{ flex: 1, minWidth: 0 }}
        />
      </Box>
    );
  },
});

type CommentInputProps = {
  setIsCommentBoxFocused: Dispatch<SetStateAction<boolean>>;
  isCommentBoxFocused: boolean;
  isSubmitting: boolean;
  onSubmitComment: () => void;
};

const CommentInput = withForm({
  props: {} as CommentInputProps,
  defaultValues: {} as CommentFormData,
  render: function CommentInput({
    form,
    setIsCommentBoxFocused,
    isCommentBoxFocused,
    isSubmitting,
    onSubmitComment,
  }) {
    const selectedCategory = useStore(
      form.store,
      (state) => state.values.category,
    );
    const submissionAttempts = useStore(
      form.store,
      (state) => state.submissionAttempts,
    );
    const isSubmitSuccessful = useStore(
      form.store,
      (state) => state.isSubmitSuccessful,
    );

    return (
      <Box
        onBlur={(e) => {
          if (
            e.relatedTarget instanceof Node &&
            e.currentTarget.contains(e.relatedTarget)
          ) {
            return;
          }

          setIsCommentBoxFocused(false);
        }}
        tabIndex={-1}
        sx={{
          padding: '1rem 1.5rem',
          flexDirection: 'column',
          alignItems: 'flex-end',
          width: '100%',
        }}
      >
        <form.Field
          name="comment"
          validators={{
            onChange: validateCommentValue,
            onSubmit: validateCommentValue,
          }}
        >
          {(field) => {
            const showError =
              submissionAttempts > 0 &&
              !isSubmitSuccessful &&
              field.state.meta.errors.length > 0;
            const helperText = showError
              ? (field.state.meta.errors[0] as string)
              : undefined;

            return (
              <TextField
                id={field.name}
                name={field.name}
                size="small"
                multiline
                fullWidth
                slotProps={{
                  input: {
                    sx: {
                      borderRadius: '4px',
                    },
                  },
                }}
                maxRows={isCommentBoxFocused ? 5 : 1}
                placeholder="Enter new comment..."
                onFocus={() => setIsCommentBoxFocused(true)}
                onBlur={() => field.handleBlur()}
                onChange={(e) => field.handleChange(e.target.value)}
                value={field.state.value}
                error={showError}
                helperText={helperText}
              />
            );
          }}
        </form.Field>
        <Collapse in={isCommentBoxFocused}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              pt: '1rem',
              gap: '1rem',
            }}
          >
            <Box>
              <Text sx={{ fontSize: '0.75rem', lineHeight: '1rem' }}>
                Will be submitted to
              </Text>
              <Text sx={{ fontWeight: 500, lineHeight: '1rem' }}>
                {selectedCategory?.value}
              </Text>
            </Box>
            <Button
              isLoading={isSubmitting}
              isDisabled={isSubmitting}
              type="button"
              onClick={onSubmitComment}
              rightIcon={<FontAwesomeIcon icon={faPaperPlane} />}
            >
              Submit Comment
            </Button>
          </Box>
        </Collapse>
      </Box>
    );
  },
});
